import { StripeCoupon, StripePublicConfig, StripeSubscriptionStatus } from "@cruisen/contract"
import styles from "./styles.module.scss"
import { formatCurrency } from "@cruisen/i18n"
import { calculateDiscountedPrice } from "../../../../../utils/discounts"
import { useMutation } from "@tanstack/react-query"
import { createStripeSubscription } from "../../../../../services/firebase-functions/billing.ts"

export const PlanDetails = ({
  onSuccess,
  isEarlyAdopter,
  isDiscountApplied,
  stripeConfigPrice,
  isStripeConfigLoading,
  subscriptionStatus,
  trialPeriodDays,
  discount,
}: {
  onSuccess: () => void
  isEarlyAdopter: boolean
  isDiscountApplied: boolean
  stripeConfigPrice: StripePublicConfig["prices"][0]
  subscriptionStatus?: StripeSubscriptionStatus
  isStripeConfigLoading: boolean
  trialPeriodDays: number
  discount: StripeCoupon | null
}) => {
  const priceAmount = stripeConfigPrice?.amount / 100
  const discountOffPercent = discount?.percent_off ?? 0
  const activePrice = isDiscountApplied
    ? calculateDiscountedPrice(priceAmount, discount)
    : priceAmount

  const { mutate: startSubscription, isPending: subscriptionStartIsPending } = useMutation({
    mutationFn: async () => {
      const createStripeSubscriptionResult = await createStripeSubscription({
        priceId: stripeConfigPrice.priceId,
        couponId: stripeConfigPrice.defaultCouponId,
      })

      if (createStripeSubscriptionResult?.subscriptionId) {
        onSuccess()
      }
    },
  })

  return (
    <div>
      {!subscriptionStatus || subscriptionStatus === "trialing" ? (
        isEarlyAdopter ? (
          <p className={styles.description}>
            Thanks for being an early adopter! Cruisen is moving to a subscription model, but you
            can keep enjoying all we have to offer with a free{" "}
            {isStripeConfigLoading ? "**" : trialPeriodDays}-day trial — no charge until it ends.
            Your free trial starts today.
          </p>
        ) : (
          <p className={styles.description}>
            Thanks for using Cruisen - the world's best sales tool for cruise advisors and their
            customers. We hope you are enjoying your{" "}
            {isStripeConfigLoading ? "**" : trialPeriodDays}-day no cost trial. Please activate your
            subscription for continued access.
          </p>
        )
      ) : (
        <p className={styles.description}>
          Thanks for trying out Cruisen - the world's best sales tool for cruise advisors and their
          customers. We hope you enjoyed your {isStripeConfigLoading ? "**" : trialPeriodDays}-day
          no cost trial. Please activate your subscription for continued access.
        </p>
      )}
      <div className={styles.planDetails}>
        <h3 className={styles.planDetailsTitle}>Plan details</h3>
        <div className={styles.planCardBorder}>
          <div className={styles.planCardWrapper}>
            <div className={styles.planCard}>
              <div className={styles.planInfo}>
                {isDiscountApplied && discountOffPercent !== 0 ? (
                  <div className={styles.discount}>
                    <p className={styles.discountText}>-{discountOffPercent}% for a year</p>
                  </div>
                ) : null}
                <div className={styles.price}>
                  <span className={styles.newPrice}>
                    {isStripeConfigLoading ? "..." : formatCurrency(activePrice)}
                  </span>
                  {isDiscountApplied && !isStripeConfigLoading ? (
                    <span className={styles.oldPrice}>{formatCurrency(priceAmount)}</span>
                  ) : null}
                </div>
                <p className={styles.period}>per month</p>
                {discount?.metadata?.maxUserRegistrationDate ? (
                  <p className={styles.benefit}>Enjoy an early adopter discount!</p>
                ) : null}
              </div>
              <div className={styles.planBreakdownWrapper}>
                <div className={styles.planBreakdown}>
                  <div className={styles.trialRow}>
                    <p>
                      {trialPeriodDays}-day trial
                      {subscriptionStatus && subscriptionStatus !== "trialing" ? " (expired)" : ""}
                    </p>
                    <p className={styles.rowPrice}>{formatCurrency(0)}</p>
                  </div>
                  <div className={styles.nextMonthRow}>
                    <p>Monthly{discount?.metadata?.maxUserRegistrationDate ? "*" : ""}</p>
                    <p className={styles.rowPrice}>
                      {isStripeConfigLoading ? "..." : formatCurrency(activePrice)}
                    </p>
                  </div>
                  {discount?.metadata?.maxUserRegistrationDate ? (
                    <p className={styles.renewalNote}>
                      *For 12 months, renews at{" "}
                      {isStripeConfigLoading ? "..." : formatCurrency(priceAmount)}/month. Cancel
                      anytime.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        className={styles.continueButton}
        disabled={isStripeConfigLoading || subscriptionStartIsPending}
        onClick={() => {
          if (subscriptionStatus) {
            onSuccess()
          } else {
            startSubscription()
          }
        }}
      >
        {subscriptionStartIsPending
          ? "Processing..."
          : subscriptionStatus
            ? "Continue"
            : "Activate trial"}
      </button>
    </div>
  )
}
